import { useStore } from '@nanostores/react';
import { ClientOnly } from 'remix-utils/client-only';
import { chatStore } from '~/lib/stores/chat';
import { classNames } from '~/utils/classNames';
import { ChatDescription } from '~/lib/persistence/ChatDescription.client';
import { Menu } from '~/components/sidebar/Menu.client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { projectId, db, getFramework, description } from '~/lib/persistence';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';

import { useNavigate, useLocation, useLoaderData } from '@remix-run/react';
import { selectionStore } from '~/lib/persistence/selectionStore';
import { getAiModelOptions } from '~/components/home/HomeView.client';
import {
  Headset,
  LogOut,
  Menu as MenuIcon,
  X,
  MessageCircle,
  Mail,
  ChevronDown,
  BookOpen,
  Sun,
  Moon,
  PanelLeftOpen,
  Gift,
} from 'lucide-react';
import { clearProjectRules } from '~/lib/stores/rules';
import { themeStore } from '~/lib/stores/theme';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { userStore } from '~/lib/stores/user';
import { claude_sonnet_4, isDev } from '~/utils/constants';
import OfferTimer, { isOfferAvailable } from '~/components/ui/OfferTimer';

// New imports for Storage Management
import { StorageManagementButton } from './StorageManagementButton.client';
import RightTopBar from '~/components/chat/RightTopBar';
import { StorageExceededModal } from '~/components/modals/StorageExceededModal.client';
import { frameworkService } from '~/lib/services/frameworkService';
import {
  techStackSelectionStore,
  setFramework,
  setLanguage,
  type Framework,
  type Language,
} from '~/lib/stores/techStackSelection';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { isMobile } from '~/utils/mobile';
import { type MenuLink } from '~/components/ui/ButtonWithDropdown';
import { useTranslation } from '~/lib/hooks/useTranslation';
import { useStorageStatus } from '~/hooks/useStorageStatus';

import { ThemeSwitcher } from '~/components/ui/ThemeSwitcher';
import { LanguageSwitcher } from '~/components/ui/LanguageSwitcher';
import { usePosthogCommonEvents } from '~/lib/hooks/posthog/usePosthogCommonEvents';
import {
  supabaseConnectionStatusStore,
  supabaseProjectNameStore,
  supabaseProjectIdStore,
} from '@/lib/stores/integrations';
import { signOut } from '@/lib/auth.client';
import { workbenchStore } from '~/lib/stores/workbench';

// Backend logout API call
export async function callLogoutAPI() {
  try {
    return await Sentry.startSpan(
      {
        name: 'callLogoutAPI',
        op: 'callLogoutAPI',
      },
      async () => {
        const response = await fetch('/auth/logout', {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Logout API failed with status: ${response.status}`);
        }

        const result = await response.json();
        console.log('[Header] Logout API response:', result);
        return result;
      },
    );
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        message: 'Error calling logout API',
      },
    });
    console.error('[Header] Error calling logout API:', error);
    throw error;
  }
}

// This will be created dynamically with translations inside the component

const formatTechStack = (framework: Framework | undefined, language: Language | undefined) => {
  if (!framework || !language) {
    return '';
  }

  const frameworkDisplay = frameworkService.formatFramework(framework);
  const languageDisplay = frameworkService.formatLanguage(language);

  return `${frameworkDisplay} + ${languageDisplay}`;
};

interface HeaderProps {
  paymentUrl: string;
  netlifyConfig: {
    NEXT_PUBLIC_NETLIFY_CLIENT_ID: string;
    NEXT_PUBLIC_NETLIFY_APP_URL: string;
  };
  githubCreds?: {
    GITHUB_CLIENT_ID: string;
  };
}

export function Header({ paymentUrl, netlifyConfig, githubCreds }: HeaderProps) {
  const { t } = useTranslation();
  const chat = useStore(chatStore);
  const theme = useStore(themeStore);
  const [techStack, setTechStack] = useState<{ framework: string; language: string } | null>(null);
  const supabaseConnectionStatus = useStore(supabaseConnectionStatusStore);
  const supabaseProjectName = useStore(supabaseProjectNameStore);
  const supabaseProjectId = useStore(supabaseProjectIdStore);
  const [open, setOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  console.log('header paymentUrl', paymentUrl);

  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const userData = useStore(userStore);
  const location = useLocation();
  const prevLocationRef = useRef(location);

  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const aiModel = useStore(selectionStore).aiModel;
  const llmProvider = useStore(selectionStore).llmProvider;
  const aiModelBaseLabel = getAiModelOptions(llmProvider).find((opt) => opt.value === aiModel)?.label || aiModel;
  const aiModelLabel = `${aiModelBaseLabel} · ${llmProvider === 'aws' ? 'AWS' : 'GCP'}`;
  const currentView = useStore(workbenchStore.currentView);

  // Check if user has exhausted all prompts
  const areAllPromptsUsed = userData.promptsUsed === userData.maxPrompts;

  const { allProjects } = useStorageStatus();

  useEffect(() => {
    if (location.pathname.includes('/chat')) {
      setIsChatOpen(true);
    } else {
      setIsChatOpen(false);
    }

    prevLocationRef.current = location;
  }, [location]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }

      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Check if IndexedDB is ready
    if (db) {
      setDbReady(true);
    }
  }, []);

  useEffect(() => {
    const getCurrentTechStack = async () => {
      const currentProjectId = projectId.get();
      console.log('[TechStack Debug] Fetching tech stack. Trigger params:', {
        dbReady,
        currentProjectId,
        isChatOpen,
        pathname: location.pathname,
        trigger: 'location change',
      });

      if (!db || !currentProjectId) {
        const framework = techStackSelectionStore.get().framework;
        const language = techStackSelectionStore.get().language;
        console.log('[TechStack Debug] No DB/ChatId - Using framework store:', { framework, language });
        setTechStack({ framework, language });
        setFramework(framework as Framework);
        setLanguage(language as Language);
        return;
      }

      try {
        console.log('[TechStack Debug] Fetching framework from DB for chatId:', currentProjectId);

        const result = await getFramework(db, currentProjectId);
        console.log('[TechStack Debug] DB Result:', result);

        if (result) {
          const { framework, language } = result;

          if (framework && language && framework !== 'unknown' && language !== 'unknown') {
            console.log('[TechStack Debug] Using valid DB values:', { framework, language });
            setTechStack({ framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
          } else {
            const framework = techStackSelectionStore.get().framework;
            const language = techStackSelectionStore.get().language;
            console.log('[TechStack Debug] DB values unknown - Using framework store:', { framework, language });
            setTechStack({ framework, language });
            setFramework(framework as Framework);
            setLanguage(language as Language);
          }
        } else {
          const framework = techStackSelectionStore.get().framework;
          const language = techStackSelectionStore.get().language;
          console.log('[TechStack Debug] No DB result - Using framework store:', { framework, language });
          setTechStack({ framework, language });
          setFramework(framework as Framework);
          setLanguage(language as Language);
        }
      } catch (error) {
        console.error('[TechStack Debug] Error fetching framework:', error);

        const framework = techStackSelectionStore.get().framework;
        const language = techStackSelectionStore.get().language;
        console.log('[TechStack Debug] Error fallback - Using framework store:', { framework, language });
        setTechStack({ framework, language });
        setFramework(framework as Framework);
        setLanguage(language as Language);
      }
    };

    console.log('[TechStack Debug] Effect triggered with pathname:', location.pathname);
    getCurrentTechStack();
  }, [
    dbReady,
    projectId.get(),
    isChatOpen,
    techStackSelectionStore.get().framework,
    techStackSelectionStore.get().language,
  ]);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isStorageExceededModalOpen, setIsStorageExceededModalOpen] = useState(false);

  const { trackCallBooked } = usePosthogCommonEvents();

  // Logout function
  const handleLogout = useCallback(async () => {
    const currentUser = userData.email;
    console.log('[Header] Logout initiated by user:', currentUser);

    setIsLoggingOut(true);

    try {
      // Call backend logout API to clear server-side session
      console.log('[Header] Calling backend logout API');

      const { error } = await signOut();

      if (error) {
        throw new Error(error.message);
      }

      // Clear user data from store
      console.log('[Header] Clearing user data from store');
      userStore.set({
        email: '',
        isSubscribed: false,
        maxPrompts: '0',
        promptsUsed: '0',
        planName: 'Free',
        name: '',
        inviteCode: '',
        topUpPrompts: '0',
        topUpPromptsUsed: '0',
        created_at: new Date(),
        image: '',
      });
      console.log('[Header] User data cleared from store');

      // Clear authentication token from localStorage
      console.log('[Header] Clearing authentication tokens from localStorage');

      // Clear all localStorage data except promptParam
      console.log('[Header] Clearing all localStorage data except promptParam');

      const promptParam = localStorage.getItem('promptParam');

      // Clear all localStorage
      localStorage.clear();

      // Restore promptParam if it existed
      if (promptParam) {
        localStorage.setItem('promptParam', promptParam);
        console.log('[Header] promptParam preserved in localStorage');
      }

      // Clear all sessionStorage data for perfect logout
      console.log('[Header] Clearing all sessionStorage data');
      sessionStorage.clear();

      toast.success('Successfully logged out');

      // Redirect to onboarding or home page
      console.log('[Header] Redirecting to onboarding page');

      navigate('/onboarding');

      console.log('[Header] Logout process completed successfully for user:', currentUser);
    } catch (error) {
      console.error('[Header] Error during logout process:', error);
      toast.error('Error occurred during logout');

      // Still attempt to redirect even if there was an error
      navigate('/onboarding');
    } finally {
      setIsLoggingOut(false);
    }
  }, [navigate, userData.email]);

  // TODO: Return null if route is onboarding
  if (pathname.includes('/onboarding') || pathname.includes('/pricing')) {
    return null;
  }

  const links: MenuLink[] = [
    {
      type: 'link',
      icon: <Mail className="w-4 h-4" />,
      title: t('header.emailUs'),
      url: 'mailto:info@dualite.dev',
    },

    {
      type: 'link',
      icon: <Headset className="w-4 h-4" />,
      title: t('header.scheduleCall'),
      url: 'https://calendly.com/singhvirohan/dualite-feedback',
    },
  ];

  const isSettingsPage = pathname.includes('/settings');

  const isHomePage = pathname === '/home';

  if (isSettingsPage) {
    return null;
  }

  return (
    <header
      className={classNames(
        'flex items-center space-between bg-dualite-background-primary pl-1 h-[var(--header-height)]',
        {
          'w-full gap-5 pr-2 border-b border-dualite-border-primary mb-3 pt-1': !chat.started,
          'w-full py-3 border-b border-dualite-border-primary pt-3': chat.started,
        },
      )}
    >
      <div className="flex flex-1 items-center gap-2">
        <div
          ref={menuRef}
          className="ml-2 flex items-center gap-2 z-logo text-dualite-text-primary cursor-pointer transition-all"
        >
          <ClientOnly>
            {() => <Menu open={open} onClose={() => setOpen(false)} projects={allProjects} paymentUrl={paymentUrl} />}
          </ClientOnly>

          {/* Mobile: Dualite logo + sidebar expand button — comes first */}
          <div className="flex items-center gap-2 md:hidden shrink-0">
            <a
              href="/home"
              style={{ background: 'linear-gradient(180deg, #131314 0%, #1D1D1F 100%)' }}
              className="w-10 h-10 rounded-xl flex items-center justify-center border border-dualite-border-primary shadow-lg transition-colors"
              onClick={() => {
                projectId.set(undefined);
                description.set(undefined);
                clearProjectRules();
              }}
            >
              <img src="/images/dualite-logo.svg" alt="Dualite" className="w-6 h-6 brightness-0 invert" />
            </a>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent('expand-sidebar'));
              }}
              className="p-2 bg-dualite-background-button rounded-lg text-alpha-elements-textTertiary hover:bg-dualite-background-buttonHover hover:text-dualite-text-primary transition-colors"
              aria-label="Open sidebar"
            >
              <PanelLeftOpen size={20} className="text-dualite-text-primary" />
            </button>
          </div>

          {chat.started ? (
            <div className="flex-1 min-w-0 px-2 flex items-center justify-center text-dualite-text-primary text-sm md:text-base">
              <ClientOnly>{() => <ChatDescription />}</ClientOnly>
            </div>
          ) : null}
          {chat.started && (
            <div className="px-2 py-1 rounded-md bg-dualite-background-button text-sm font-medium text-dualite-text-secondary border border-dualite-border-primary hidden sm:block">
              {formatTechStack(techStack?.framework as Framework, techStack?.language as Language)}
            </div>
          )}
          {/* {supabaseConnectionStatus.isConnected && supabaseProjectId && chat.started && (
            <div
              className="px-2 py-1 rounded-md bg-dualite-background-button text-sm font-medium text-dualite-text-secondary border border-dualite-border-primary hidden sm:block cursor-pointer hover:bg-dualite-background-button-hover"
              onClick={() => {
                if (supabaseConnectionStatus.isConnected && supabaseProjectId) {
                  const url = `https://supabase.com/dashboard/project/${supabaseProjectId}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }
              }}
            >
              Database
            </div>
          )} */}
        </div>

        {isDev && (
          <div className="ml-6 w-fit text-sm text-dualite-text-secondary hidden md:block">Model: {aiModelLabel}</div>
        )}
      </div>

      <div className="flex gap-2">
        {/* Desktop version - hide on mobile */}

        <div className="hidden md:flex gap-2 items-center">
          {chat.started && currentView === 'canvas' ? (
            <>
              <p className="text-xs text-dualite-text-secondary max-w-[315px] leading-tight pr-2">
                {t('header.canvasAnnotationHint')}{' '}
                <a
                  href="https://alphadocs.dualite.dev/tips-and-techniques/interaction-mode"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-dualite-text-primary transition-colors"
                >
                  {t('header.learnMore')}
                </a>
              </p>
              <ThemeSwitcher />
              <LanguageSwitcher />
              <div className="px-1" />
            </>
          ) : (
            <>
              <ThemeSwitcher />

              <LanguageSwitcher />
              {chat.started ? (
                <RightTopBar netlifyConfig={netlifyConfig} githubCreds={githubCreds} paymentUrl={paymentUrl} />
              ) : (
                <>
                  {/* Only show subscription status once we have user data */}
                  {userData.email !== 'user_email@dummy.dualite' &&
                    window.location.pathname !== '/billing' &&
                    !window.location.pathname.includes('/chat') && (
                      <>
                        {userData.isSubscribed ? (
                          <>
                            {areAllPromptsUsed && (
                              <div
                                className="cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium text-dualite-text-primary bg-dualite-background-button hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap"
                                onClick={() => {
                                  setIsProUpgradeModalOpen(true);
                                }}
                              >
                                {t('header.exhausted')}
                              </div>
                            )}

                            {userData.planName.includes('Launch') ? (
                              <div className="cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap">
                                {t('header.planUser', { planName: planNameCleaner(userData.planName).planName })}
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover transition-colors duration-150 whitespace-nowrap"
                                onClick={() => {
                                  window.open(`${paymentUrl}/portal?email=${userData.email}&type=upgrade`);
                                }}
                              >
                                {t('header.upgradeToLaunch')}
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            className="cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover transition-colors duration-150 whitespace-nowrap"
                            onClick={() => {
                              setIsProUpgradeModalOpen(true);
                            }}
                          >
                            {t('header.upgradeToPro')}
                            {isOfferAvailable() && <OfferTimer mini />}
                          </div>
                        )}
                      </>
                    )}
                  {/* Referral settings */}
                  <button
                    type="button"
                    onClick={() => navigate('/settings/referral')}
                    className="flex items-center justify-center w-[35px] h-[35px] cursor-pointer rounded-md bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150"
                    aria-label="Open referrals"
                    title="Referrals"
                  >
                    <Gift size={16} aria-hidden="true" />
                  </button>

                  {/* Discord community button - only on /home */}
                  {isHomePage && (
                    <a
                      href="https://discord.gg/vSBjB88PVy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-[35px] h-[35px] cursor-pointer rounded-md bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150"
                      aria-label="Join our Discord community"
                    >
                      <svg
                        id="Discord-Logo"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 126.644 96"
                        width={16}
                        height={16}
                        aria-hidden="true"
                        className="text-current"
                      >
                        <path
                          id="Discord-Symbol-White"
                          fill="currentColor"
                          d="M81.15,0c-1.2376,2.1973-2.3489,4.4704-3.3591,6.794-9.5975-1.4396-19.3718-1.4396-28.9945,0-.985-2.3236-2.1216-4.5967-3.3591-6.794-9.0166,1.5407-17.8059,4.2431-26.1405,8.0568C2.779,32.5304-1.6914,56.3725.5312,79.8863c9.6732,7.1476,20.5083,12.603,32.0505,16.0884,2.6014-3.4854,4.8998-7.1981,6.8698-11.0623-3.738-1.3891-7.3497-3.1318-10.8098-5.1523.9092-.6567,1.7932-1.3386,2.6519-1.9953,20.281,9.547,43.7696,9.547,64.0758,0,.8587.7072,1.7427,1.3891,2.6519,1.9953-3.4601,2.0457-7.0718,3.7632-10.835,5.1776,1.97,3.8642,4.2683,7.5769,6.8698,11.0623,11.5419-3.4854,22.3769-8.9156,32.0509-16.0631,2.626-27.2771-4.496-50.9172-18.817-71.8548C98.9811,4.2684,90.1918,1.5659,81.1752.0505l-.0252-.0505ZM42.2802,65.4144c-6.2383,0-11.4159-5.6575-11.4159-12.6535s4.9755-12.6788,11.3907-12.6788,11.5169,5.708,11.4159,12.6788c-.101,6.9708-5.026,12.6535-11.3907,12.6535ZM84.3576,65.4144c-6.2637,0-11.3907-5.6575-11.3907-12.6535s4.9755-12.6788,11.3907-12.6788,11.4917,5.708,11.3906,12.6788c-.101,6.9708-5.026,12.6535-11.3906,12.6535Z"
                        />
                      </svg>
                    </a>
                  )}

                  {/* Storage Management Button will go here */}
                  <ClientOnly>
                    {() => <StorageManagementButton setIsStorageExceededModalOpen={setIsStorageExceededModalOpen} />}
                  </ClientOnly>

                  {/* setting icon */}
                  <button
                    onClick={() => {
                      navigate('/settings');
                    }}
                    className="flex items-center justify-center gap-2 h-[35px] cursor-pointer rounded-md px-3 bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap text-xs font-medium"
                  >
                    <i className="i-ph:gear-duotone text-[17px] text-current" />
                    {/* {t('header.settings', { defaultValue: 'Settings' })} */}
                  </button>

                  {/* Logout button - only show if user is logged in */}
                  {userData.email && (
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className={`flex items-center justify-center gap-2 h-[35px] rounded-md px-3 bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150 whitespace-nowrap text-xs font-medium ${
                        isLoggingOut ? 'opacity-80 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <LogOut className="w-[14px] h-[14px] text-current" />

                      {isLoggingOut
                        ? t('navigation.loggingOut', { defaultValue: 'Logging out' })
                        : t('navigation.logout')}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* Mobile version - show only hamburger menu */}
        <div className="flex md:hidden gap-2 items-center relative" ref={mobileMenuRef}>
          {/* Theme switcher - outside hamburger menu */}
          {!chat.started && <ThemeSwitcher />}

          {/* Language switcher - outside hamburger menu */}
          {!chat.started && <LanguageSwitcher />}

          {/* Hamburger menu button */}
          {!chat.started && (
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 bg-dualite-background-button rounded-lg text-dualite-text-primary hover:bg-dualite-background-buttonHover hover:text-dualite-text-secondary transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-current" />
              ) : (
                <MenuIcon className="w-5 h-5 text-current" />
              )}
            </button>
          )}

          {chat.started && isMobile() && (
            <button
              onClick={() => chatStore.setKey('showChat', !chat.showChat)}
              className="z-[2000] flex items-center justify-center cursor-pointer bg-dualite-background-cta text-[var(--dualite-text-cta)] px-3 py-1 rounded-md transition-all duration-200 border border-gray-200 shadow-sm hover:bg-dualite-background-cta-hover mr-4 shrink-0"
            >
              {chat.showChat ? t('header.preview') : t('header.chat')}
            </button>
          )}

          {/* Mobile dropdown menu */}
          {isMobileMenuOpen && !chat.started && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-dualite-background-tertiary border border-dualite-border-secondary rounded-lg shadow-xl shadow-dualite-background-button backdrop-blur-sm z-50">
              <div className="py-1">
                {/* Model name - only show in dev */}
                {isDev && (
                  <div className="px-4 py-3 text-sm text-dualite-text-secondary border-b border-dualite-border-secondary">
                    Model: {aiModelLabel}
                  </div>
                )}
                {/* Subscription Status / Upgrade button */}
                {userData.email !== 'user_email@dummy.dualite' &&
                  window.location.pathname !== '/billing' &&
                  !window.location.pathname.includes('/chat') && (
                    <div className="px-4 py-3 border-b border-dualite-border-secondary">
                      {userData.isSubscribed && areAllPromptsUsed ? (
                        <button
                          className="w-full cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium text-dualite-text-primary bg-dualite-background-button hover:bg-dualite-background-button-hover transition-colors duration-150"
                          onClick={() => {
                            setIsProUpgradeModalOpen(true);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t('header.exhausted')}
                        </button>
                      ) : userData.isSubscribed ? (
                        <div className="text-center cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150">
                          {t('header.planUser', { planName: planNameCleaner(userData.planName).planName })}
                        </div>
                      ) : (
                        <button
                          className="w-full cursor-pointer flex items-center justify-center gap-2 h-[35px] rounded-md px-3 text-xs font-medium bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover transition-colors duration-150"
                          onClick={() => {
                            setIsProUpgradeModalOpen(true);
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          {t('header.upgradeToPro')}
                          {isOfferAvailable() && <OfferTimer mini />}
                        </button>
                      )}
                    </div>
                  )}
                {/* Storage Management */}
                <div className="px-4 py-2 border-b border-dualite-border-secondary">
                  <ClientOnly>
                    {() => (
                      <div onClick={() => setIsMobileMenuOpen(false)}>
                        <StorageManagementButton setIsStorageExceededModalOpen={setIsStorageExceededModalOpen} />
                      </div>
                    )}
                  </ClientOnly>
                </div>
                {/* Tutorial */}
                <a
                  href="https://alphadocs.dualite.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-all duration-200 text-dualite-text-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <BookOpen className="w-4 h-4" />
                  Tutorial
                </a>
                {/* Support - Email Us */}
                <a
                  href="mailto:info@dualite.dev"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-all duration-200 text-dualite-text-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Mail className="w-4 h-4" />
                  {t('header.emailUs')}
                </a>
                {/* Support - Schedule Call */}
                <a
                  href="https://calendly.com/singhvirohan/dualite-feedback"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-all duration-200 text-dualite-text-primary border-b border-dualite-border-primary"
                  onClick={(e) => {
                    e.preventDefault();

                    try {
                      trackCallBooked('header_mobile');
                    } finally {
                      window.open(
                        'https://calendly.com/singhvirohan/dualite-feedback',
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <Headset className="w-4 h-4" />
                  {t('header.scheduleCall')}
                </a>

                {/* Settings */}
                <button
                  className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-all duration-200 text-dualite-text-primary"
                  onClick={() => {
                    navigate('/settings');
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <i className="i-ph:gear-duotone text-lg" />
                  {t('header.settings')}
                </button>
                {/* Discord community - only on /home */}
                {isHomePage && (
                  <a
                    href="https://discord.gg/vSBjB88PVy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-colors duration-150 text-dualite-text-primary border-b border-dualite-border-primary"
                    aria-label="Join our Discord community"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <svg
                      id="Discord-Logo"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 126.644 96"
                      width={16}
                      height={16}
                      aria-hidden="true"
                    >
                      <path
                        id="Discord-Symbol-White"
                        fill="currentColor"
                        d="M81.15,0c-1.2376,2.1973-2.3489,4.4704-3.3591,6.794-9.5975-1.4396-19.3718-1.4396-28.9945,0-.985-2.3236-2.1216-4.5967-3.3591-6.794-9.0166,1.5407-17.8059,4.2431-26.1405,8.0568C2.779,32.5304-1.6914,56.3725.5312,79.8863c9.6732,7.1476,20.5083,12.603,32.0505,16.0884,2.6014-3.4854,4.8998-7.1981,6.8698-11.0623-3.738-1.3891-7.3497-3.1318-10.8098-5.1523.9092-.6567,1.7932-1.3386,2.6519-1.9953,20.281,9.547,43.7696,9.547,64.0758,0,.8587.7072,1.7427,1.3891,2.6519,1.9953-3.4601,2.0457-7.0718,3.7632-10.835,5.1776,1.97,3.8642,4.2683,7.5769,6.8698,11.0623,11.5419-3.4854,22.3769-8.9156,32.0509-16.0631,2.626-27.2771-4.496-50.9172-18.817-71.8548C98.9811,4.2684,90.1918,1.5659,81.1752.0505l-.0252-.0505ZM42.2802,65.4144c-6.2383,0-11.4159-5.6575-11.4159-12.6535s4.9755-12.6788,11.3907-12.6788,11.5169,5.708,11.4159,12.6788c-.101,6.9708-5.026,12.6535-11.3907,12.6535ZM84.3576,65.4144c-6.2637,0-11.3907-5.6575-11.3907-12.6535s4.9755-12.6788,11.3907-12.6788,11.4917,5.708,11.3906,12.6788c-.101,6.9708-5.026,12.6535-11.3906,12.6535Z"
                      />
                    </svg>
                    Join our Discord
                  </a>
                )}
                {/* Logout - only show if user is logged in */}
                {userData.email && (
                  <div className="border-t border-dualite-border-primary">
                    <button
                      className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-dualite-background-button-hover transition-all duration-200 text-dualite-text-primary ${
                        isLoggingOut ? 'opacity-80 cursor-not-allowed' : ''
                      }`}
                      disabled={isLoggingOut}
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      {isLoggingOut ? (
                        <div className="w-4 h-4 border-2 border-dualite-text-primary/30 border-t-dualite-text-primary rounded-full animate-spin" />
                      ) : (
                        <LogOut className="w-4 h-4" />
                      )}
                      {isLoggingOut
                        ? t('navigation.loggingOut', { defaultValue: 'Logging out...' })
                        : t('navigation.logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        isTopUp={userData.isSubscribed && areAllPromptsUsed}
        paymentUrl={paymentUrl}
      />

      <StorageExceededModal
        setIsProUpgradeModalOpen={setIsProUpgradeModalOpen}
        isOpen={isStorageExceededModalOpen}
        onClose={() => setIsStorageExceededModalOpen(false)}
      />

      {/* <div className="ml-6 w-fit text-sm text-dualite-text-secondary">
        <IssueDropdown framework="react" />
      </div> */}
      {/* {chat.started && (
        <ClientOnly>
          {() => (
            <div className="mr-1">
              <HeaderActionButtons />
            </div>
          )}
        </ClientOnly>
      )} */}
      {/* {chat.started && <RightTopBar />} */}

      {/* <button
        onClick={() => setIsDeployModalOpen(true)}
        className="px-4 py-2 bg-blue-500 text-dualite-text-primary rounded hover:bg-blue-600"
      >
      </button> */}

      {/* <DeployModal
        isOpen={isDeployModalOpen}
        onClose={() => setIsDeployModalOpen(false)}
        accessToken="YOUR_OAUTH_TOKEN" // TODO: Get this from OAuth flow
      /> */}
    </header>
  );
}
